import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type PendingFileSaveCopy = {
  ready: string;
  save: string;
  dismiss: string;
  /** The share sheet refused the file; it is still held. */
  failed: string;
  retry: string;
  /** Over the share size limit, with the file's own URL to open. */
  tooLarge: string;
  /** Over the share size limit, with nothing else to offer. */
  useComputer: string;
  /** This browser can't share the file; its own URL can be opened. */
  openToSave: string;
  /** The share sheet can't take the file and there is no URL to open. */
  cantShare: string;
  open: string;
};

const COPY: Record<SupportedLocale, PendingFileSaveCopy> = {
  en: {
    ready: 'Ready to save',
    save: 'Save or send',
    dismiss: 'Dismiss',
    failed: "Couldn't open sharing",
    retry: 'Try again',
    tooLarge: 'Too large to share from here',
    useComputer: 'Too large for this phone. Use a computer.',
    openToSave: 'Open it to save or send',
    cantShare: "Can't share this file here. Use a computer.",
    open: 'Open file',
  },
  ar: {
    ready: 'جاهز للحفظ',
    save: 'حفظ أو إرسال',
    dismiss: 'إغلاق',
    failed: 'تعذّر فتح المشاركة',
    retry: 'حاول مرة أخرى',
    tooLarge: 'كبير جدًا للمشاركة من هنا',
    useComputer: 'كبير جدًا لهذا الهاتف. استخدم كمبيوتر.',
    openToSave: 'افتحه للحفظ أو الإرسال',
    cantShare: 'تعذّرت مشاركة هذا الملف هنا. استخدم كمبيوتر.',
    open: 'فتح الملف',
  },
  es: {
    ready: 'Listo para guardar',
    save: 'Guardar o enviar',
    dismiss: 'Cerrar',
    failed: 'No se pudo abrir Compartir',
    retry: 'Reintentar',
    tooLarge: 'Demasiado grande para compartir desde aquí',
    useComputer: 'Demasiado grande para este teléfono. Usa un ordenador.',
    openToSave: 'Ábrelo para guardarlo o enviarlo',
    cantShare: 'No se puede compartir este archivo aquí. Usa un ordenador.',
    open: 'Abrir archivo',
  },
  'pt-BR': {
    ready: 'Pronto para salvar',
    save: 'Salvar ou enviar',
    dismiss: 'Fechar',
    failed: 'Não foi possível abrir o compartilhamento',
    retry: 'Tentar de novo',
    tooLarge: 'Grande demais para compartilhar daqui',
    useComputer: 'Grande demais para este celular. Use um computador.',
    openToSave: 'Abra para salvar ou enviar',
    cantShare:
      'Não é possível compartilhar este arquivo aqui. Use um computador.',
    open: 'Abrir arquivo',
  },
  fr: {
    ready: 'Prêt à enregistrer',
    save: 'Enregistrer ou envoyer',
    dismiss: 'Fermer',
    failed: "Impossible d'ouvrir le partage",
    retry: 'Réessayer',
    tooLarge: 'Trop volumineux pour être partagé ici',
    useComputer: 'Trop volumineux pour ce téléphone. Utilisez un ordinateur.',
    openToSave: "Ouvrez-le pour l'enregistrer ou l'envoyer",
    cantShare: 'Impossible de partager ce fichier ici. Utilisez un ordinateur.',
    open: 'Ouvrir le fichier',
  },
  it: {
    ready: 'Pronto da salvare',
    save: 'Salva o invia',
    dismiss: 'Chiudi',
    failed: 'Impossibile aprire la condivisione',
    retry: 'Riprova',
    tooLarge: 'Troppo grande da condividere da qui',
    useComputer: 'Troppo grande per questo telefono. Usa un computer.',
    openToSave: 'Aprilo per salvarlo o inviarlo',
    cantShare: 'Impossibile condividere questo file qui. Usa un computer.',
    open: 'Apri file',
  },
  ko: {
    ready: '저장 준비 완료',
    save: '저장 또는 보내기',
    dismiss: '닫기',
    failed: '공유를 열 수 없습니다',
    retry: '다시 시도',
    tooLarge: '여기서 공유하기에는 너무 큽니다',
    useComputer: '이 휴대폰에는 너무 큽니다. 컴퓨터를 사용하세요.',
    openToSave: '열어서 저장하거나 보내세요',
    cantShare: '여기서는 이 파일을 공유할 수 없습니다. 컴퓨터를 사용하세요.',
    open: '파일 열기',
  },
  de: {
    ready: 'Bereit zum Sichern',
    save: 'Sichern oder senden',
    dismiss: 'Schließen',
    failed: 'Teilen konnte nicht geöffnet werden',
    retry: 'Erneut versuchen',
    tooLarge: 'Zu groß zum Teilen von hier',
    useComputer: 'Zu groß für dieses Telefon. Nutze einen Computer.',
    openToSave: 'Öffnen, um zu sichern oder zu senden',
    cantShare:
      'Diese Datei kann hier nicht geteilt werden. Nutze einen Computer.',
    open: 'Datei öffnen',
  },
  ja: {
    ready: '保存の準備ができました',
    save: '保存または送信',
    dismiss: '閉じる',
    failed: '共有を開けませんでした',
    retry: 'もう一度試す',
    tooLarge: 'ここから共有するには大きすぎます',
    useComputer:
      'このスマートフォンには大きすぎます。パソコンを使ってください。',
    openToSave: '開いて保存または送信',
    cantShare:
      'ここではこのファイルを共有できません。パソコンを使ってください。',
    open: 'ファイルを開く',
  },
  hi: {
    ready: 'सेव करने के लिए तैयार',
    save: 'सेव करें या भेजें',
    dismiss: 'बंद करें',
    failed: 'शेयर नहीं खुल सका',
    retry: 'फिर कोशिश करें',
    tooLarge: 'यहाँ से शेयर करने के लिए बहुत बड़ी है',
    useComputer: 'इस फ़ोन के लिए बहुत बड़ी है। कंप्यूटर इस्तेमाल करें।',
    openToSave: 'सेव करने या भेजने के लिए खोलें',
    cantShare: 'यह फ़ाइल यहाँ से शेयर नहीं हो सकती। कंप्यूटर इस्तेमाल करें।',
    open: 'फ़ाइल खोलें',
  },
  ru: {
    ready: 'Готово к сохранению',
    save: 'Сохранить или отправить',
    dismiss: 'Закрыть',
    failed: 'Не удалось открыть «Поделиться»',
    retry: 'Повторить',
    tooLarge: 'Слишком большой, чтобы поделиться отсюда',
    useComputer: 'Слишком большой для этого телефона. Используйте компьютер.',
    openToSave: 'Откройте, чтобы сохранить или отправить',
    cantShare: 'Этим файлом нельзя поделиться отсюда. Используйте компьютер.',
    open: 'Открыть файл',
  },
  zh: {
    ready: '可以保存了',
    save: '保存或发送',
    dismiss: '关闭',
    failed: '无法打开共享',
    retry: '重试',
    tooLarge: '文件太大，无法从这里共享',
    useComputer: '文件太大，此手机无法保存。请使用电脑。',
    openToSave: '打开后即可保存或发送',
    cantShare: '无法在这里共享此文件。请使用电脑。',
    open: '打开文件',
  },
  'zh-TW': {
    ready: '可以儲存了',
    save: '儲存或傳送',
    dismiss: '關閉',
    failed: '無法開啟分享',
    retry: '再試一次',
    tooLarge: '檔案太大，無法從這裡分享',
    useComputer: '檔案太大，這支手機無法儲存。請使用電腦。',
    openToSave: '開啟後即可儲存或傳送',
    cantShare: '無法在這裡分享此檔案。請使用電腦。',
    open: '開啟檔案',
  },
  vi: {
    ready: 'Sẵn sàng để lưu',
    save: 'Lưu hoặc gửi',
    dismiss: 'Đóng',
    failed: 'Không mở được chia sẻ',
    retry: 'Thử lại',
    tooLarge: 'Quá lớn để chia sẻ từ đây',
    useComputer: 'Quá lớn cho điện thoại này. Hãy dùng máy tính.',
    openToSave: 'Mở để lưu hoặc gửi',
    cantShare: 'Không thể chia sẻ tệp này ở đây. Hãy dùng máy tính.',
    open: 'Mở tệp',
  },
  id: {
    ready: 'Siap disimpan',
    save: 'Simpan atau kirim',
    dismiss: 'Tutup',
    failed: 'Tidak dapat membuka berbagi',
    retry: 'Coba lagi',
    tooLarge: 'Terlalu besar untuk dibagikan dari sini',
    useComputer: 'Terlalu besar untuk ponsel ini. Gunakan komputer.',
    openToSave: 'Buka untuk menyimpan atau mengirim',
    cantShare: 'File ini tidak dapat dibagikan dari sini. Gunakan komputer.',
    open: 'Buka file',
  },
};

export function getPendingFileSaveCopy(
  locale?: string | null
): PendingFileSaveCopy {
  return COPY[normalizeLocale(locale)];
}
